// src/Components/Sidebar.js
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../images/CloudkidTextLogo-White.png';

// Icons
import { ReactComponent as ProjectsIcon } from '../assets/icons/projects.svg';
import { ReactComponent as ReleasesIcon } from '../assets/icons/releases.svg';
import { ReactComponent as ArtistsIcon } from '../assets/icons/artists.svg';
import { ReactComponent as TemplatesIcon } from '../assets/icons/templates.svg';
import { ReactComponent as InvoicesIcon } from '../assets/icons/invoices.svg';
import { ReactComponent as ContractIcon } from '../assets/icons/contract.svg';
import { ReactComponent as CalendarIcon }  from '../assets/icons/calendar.svg';
import { ReactComponent as YouTubeIcon }   from '../assets/icons/youtube.svg';
import { ReactComponent as ChecklistsIcon } from '../assets/icons/checklists.svg';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const activePath = location.pathname;



  const name = localStorage.getItem('name');

  // Persisted collapsibles
  const [tasksOpen, setTasksOpen] = useState(() => {
    const saved = localStorage.getItem('tasksOpen');
    return saved === 'true';
  });
  const [artistDetailsOpen, setArtistDetailsOpen] = useState(() => {
    const saved = localStorage.getItem('artistDetailsOpen');
    return saved === 'true';
  });

  const toggleTasksOpen = () => {
    const next = !tasksOpen;
    setTasksOpen(next);
    localStorage.setItem('tasksOpen', String(next));
  };

  const toggleArtistDetailsOpen = () => {
    const next = !artistDetailsOpen;
    setArtistDetailsOpen(next);
    localStorage.setItem('artistDetailsOpen', String(next));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('email');
    localStorage.removeItem('name');
    localStorage.removeItem('role');
    localStorage.removeItem('tasksOpen');
    localStorage.removeItem('artistDetailsOpen');
    navigate('/login');
  };

  const getLinkColor = (path) => (activePath === path ? '#ffffff' : '#999999');

  const tasksColor = '#999999';
  const sectionLabelColor = '#999999'; // for collapsible headings

    // Hide the entire sidebar in guest mode (?guest=1 or ?access=<token> in the URL)
  const params = new URLSearchParams(window.location.search);
  const guest = params.get('guest') === '1' || params.has('access');
  if (guest) return null;

  return (
    <div
      className="side-nav"
      style={{
        backgroundColor: '#1e1e1e',
        width: '180px',
        height: '100vh',
        padding: '40px 20px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflowY: 'auto',
      }}
    >
      {/* Logo */}
      <img
        src={logo}
        alt="Cloud Kid Logo"
        style={{ width: '80%', height: 'auto', marginBottom: '20px', display: 'block' }}
      />

      {/* Navigation */}
      <nav className="nav-content" style={{ width: '100%' }}>
        <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>

          {/* Project Overview */}
          <li style={{ marginBottom: '10px' }}>
            <Link
              to="/project-overview"
              style={{
                color: getLinkColor('/project-overview'),
                textDecoration: 'none',
                fontWeight: activePath === '/project-overview' ? 'bold' : 'normal',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ProjectsIcon
                style={{
                  fill: getLinkColor('/project-overview'),
                  width: '16px',
                  height: '16px',
                  marginRight: '8px',
                }}
              />
              Project Overview
            </Link>
          </li>

          {/* Release Overview */}
          <li style={{ marginBottom: '10px' }}>
            <Link
              to="/release-overview"
              style={{
                color: getLinkColor('/release-overview'),
                textDecoration: 'none',
                fontWeight: activePath === '/release-overview' ? 'bold' : 'normal',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ReleasesIcon
                style={{
                  fill: getLinkColor('/release-overview'),
                  width: '16px',
                  height: '16px',
                  marginRight: '8px',
                }}
              />
              Release Overview
            </Link>
          </li>

          {/* Calendar */}
          <li style={{ marginBottom: '10px' }}>
            <Link
              to="/calendar"
              style={{
                color: getLinkColor('/calendar'),
                textDecoration: 'none',
                fontWeight: activePath === '/calendar' ? 'bold' : 'normal',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <CalendarIcon
                style={{
                  fill: getLinkColor('/calendar'),
                  width: '16px',
                  height: '16px',
                  marginRight: '8px',
                }}
              />
              Calendar View
            </Link>
          </li>

          {/* YouTube View */}
          <li style={{ marginBottom: '10px' }}>
            <Link
              to="/youtube"
              style={{
                color: getLinkColor('/youtube'),
                textDecoration: 'none',
                fontWeight: activePath === '/youtube' ? 'bold' : 'normal',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <YouTubeIcon
                style={{
                  fill: getLinkColor('/youtube'),
                  width: '16px',
                  height: '16px',
                  marginRight: '8px',
                }}
              />
              YouTube View
            </Link>
          </li>

          {/* Invoices */}
          <li style={{ marginBottom: '10px' }}>
            <Link
              to="/invoices"
              style={{
                color: getLinkColor('/invoices'),
                textDecoration: 'none',
                fontWeight: activePath === '/invoices' ? 'bold' : 'normal',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <InvoicesIcon
                style={{
                  fill: getLinkColor('/invoices'),
                  width: '16px',
                  height: '16px',
                  marginRight: '8px',
                }}
              />
              Invoices
            </Link>
          </li>

          {/* Contract Tracking */}
          <li style={{ marginBottom: '10px' }}>
            <Link
              to="/contract-tracking"
              style={{
                color: getLinkColor('/contract-tracking'),
                textDecoration: 'none',
                fontWeight: activePath === '/contract-tracking' ? 'bold' : 'normal',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ContractIcon
                style={{
                  fill: getLinkColor('/contract-tracking'),
                  color: getLinkColor('/contract-tracking'),
                  width: '16px',
                  height: '16px',
                  marginRight: '8px',
                }}
              />
              Contract Tracking
            </Link>
          </li>

          {/* Artist Details (collapsible) */}
          <li style={{ marginBottom: '10px' }}>
            <div
              onClick={toggleArtistDetailsOpen}
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: sectionLabelColor,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {/* Using the Artists icon for the section label */}
                <ArtistsIcon
                  style={{
                    fill: sectionLabelColor,
                    width: '16px',
                    height: '16px',
                    marginRight: '8px',
                  }}
                />
                Artist Details
              </div>
              <span style={{ marginLeft: '8px', color: sectionLabelColor, fontSize: '10px' }}>
                {artistDetailsOpen ? '▼' : '▶'}
              </span>
            </div>

            {artistDetailsOpen && (
              <ul style={{ listStyleType: 'none', padding: 0, margin: '8px 0 0 16px' }}>
                <li style={{ marginBottom: '10px' }}>
                  <Link
                    to="/artists"
                    style={{
                      color: getLinkColor('/artists'),
                      textDecoration: 'none',
                      fontWeight: activePath === '/artists' ? 'bold' : 'normal',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {/* No icon here per request */}
                    Artists
                  </Link>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <Link
                    to="/writers"
                    style={{
                      color: getLinkColor('/writers'),
                      textDecoration: 'none',
                      fontWeight: activePath === '/writers' ? 'bold' : 'normal',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {/* No icon here per request */}
                    Writers
                  </Link>
                </li>
              </ul>
            )}
          </li>

          {/* Tasks (collapsible) */}
          <li style={{ marginBottom: '10px' }}>
            <div
              onClick={toggleTasksOpen}
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: tasksColor,
                fontWeight: 'normal',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <ChecklistsIcon
                  style={{
                    fill: tasksColor,
                    width: '16px',
                    height: '16px',
                    marginRight: '8px',
                  }}
                />
                Tasks
              </div>
              <span style={{ marginLeft: '8px', color: tasksColor, fontSize: '10px' }}>
                {tasksOpen ? '▼' : '▶'}
              </span>
            </div>

            {tasksOpen && (
              <ul style={{ listStyleType: 'none', padding: 0, margin: '8px 0 0 16px' }}>
                <li style={{ marginBottom: '10px' }}>
                  <Link
                    to="/operations-tasks"
                    style={{
                      color: getLinkColor('/operations-tasks'),
                      textDecoration: 'none',
                      fontWeight: activePath === '/operations-tasks' ? 'bold' : 'normal',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    Operations Tasks
                  </Link>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <Link
                    to="/marketing-tasks"
                    style={{
                      color: getLinkColor('/marketing-tasks'),
                      textDecoration: 'none',
                      fontWeight: activePath === '/marketing-tasks' ? 'bold' : 'normal',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    Marketing Tasks
                  </Link>
                </li>
              </ul>
            )}
          </li>

          {/* ToDo Templates */}
          <li style={{ marginBottom: '10px' }}>
            <Link
              to="/todo-templates"
              style={{
                color: getLinkColor('/todo-templates'),
                textDecoration: 'none',
                fontWeight: activePath === '/todo-templates' ? 'bold' : 'normal',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <TemplatesIcon
                style={{
                  fill: getLinkColor('/todo-templates'),
                  width: '16px',
                  height: '16px',
                  marginRight: '8px',
                }}
              />
              ToDo Templates
            </Link>
          </li>
          
        </ul>
      </nav>

      

      {/* User + Logout */}
      <div style={{ marginTop: 20, textAlign: 'center', color: '#ccc', fontSize: '0.85rem' }}>
        {name && (
          <p style={{ margin: '5px 0' }}>
            Logged in as <strong>{name}</strong>
          </p>
        )}
        <button
          onClick={handleLogout}
          style={{
            marginTop: 8,
            padding: '6px 12px',
            backgroundColor: '#333',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          Logout
        </button>
      </div>
    </div>
  );
};

export default Sidebar;